import type { Player } from "@cloudnest/horror-race-engine";
import { getHorrorPlayerToken } from "../utils/horror-tokens";

interface PlayerListProps {
  readonly players: readonly Player[];
  readonly turnOrder: readonly number[];
  readonly currentTurnIndex: number;
  readonly myPlayerId: string | null;
  readonly garnets: Readonly<Record<string, number>>;
  readonly showSecretFog?: boolean;
  /** Hide the internal "플레이어" heading when rendered inside the roster popup
   * (T16), which provides its own header. */
  readonly showHeading?: boolean;
}

/**
 * Per-player roster rows (seat color + garnet count). Rendered inside the
 * expandable top-banner popup (T16) — no longer at the bottom of the screen.
 * Long nicknames truncate with an ellipsis so they never push the garnet count
 * or markers off the row (full name via title).
 */
export function PlayerList({
  players,
  turnOrder,
  currentTurnIndex,
  myPlayerId,
  garnets,
  showSecretFog = false,
  showHeading = true,
}: PlayerListProps) {
  const currentPlayerIndex = turnOrder[currentTurnIndex];

  return (
    <div className="flex flex-col gap-1 p-2">
      {showHeading && (
        <h3
          className="mb-1 px-1 font-semibold text-sm"
          style={{ color: "var(--cn-horror-text)" }}
        >
          플레이어
        </h3>
      )}
      {players.map((player, index) => {
        const isCurrent = index === currentPlayerIndex;
        const isMe = player.id === myPlayerId;
        const accent = getHorrorPlayerToken(index);

        return (
          <div
            key={player.id}
            data-testid="horror-player-row"
            data-player-id={player.id}
            data-player-index={index}
            data-token-slot={(index % 12) + 1}
            aria-current={isCurrent ? "true" : undefined}
            data-seat-color={accent}
            data-is-current={isCurrent ? "true" : "false"}
            className="flex items-center justify-between gap-2 rounded px-2 py-1.5"
            style={{
              backgroundColor: isCurrent
                ? "var(--cn-horror-track)"
                : "transparent",
              border: `1px solid ${isMe ? accent : "transparent"}`,
            }}
          >
            <div className="flex min-w-0 flex-1 items-center gap-1.5">
              {isCurrent && (
                <span className="shrink-0" style={{ color: accent }}>
                  ▶
                </span>
              )}
              <span
                data-testid="horror-player-name"
                className="truncate"
                title={player.name}
                style={{ color: accent }}
              >
                {player.name}
                {isMe && " (나)"}
              </span>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {showSecretFog && (
                <span
                  data-testid="secret-fog-indicator"
                  className="text-xs"
                  style={{ color: "var(--cn-horror-text-muted)" }}
                >
                  🔒 ?·?
                </span>
              )}
              <span
                className="text-sm"
                style={{ color: "var(--cn-horror-finished-flash)" }}
              >
                {garnets[player.id] ?? 0} 가넷
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
